import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { fetchSession, logout as logoutRequest } from '../services/auth'
import type { AuthUser, LeaguePlayer } from '../types'

interface SessionValue {
  user: AuthUser | null
  leaguePlayer: LeaguePlayer | null
  isAdmin: boolean
  loading: boolean
  refresh: () => Promise<void>
  signOut: () => Promise<void>
}

const SessionContext = createContext<SessionValue | null>(null)

/**
 * Loads the Discord session once and shares it with the whole tree.
 *
 * @remarks
 * `isAdmin` here only drives what the UI offers; every mutation is re-checked
 * server-side, so a tampered client gains nothing.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [leaguePlayer, setLeaguePlayer] = useState<LeaguePlayer | null>(null)
  const [loading, setLoading] = useState(true)

  const refresh = useCallback(async () => {
    const session = await fetchSession()
    setUser(session.user)
    setLeaguePlayer(session.leaguePlayer)
    setLoading(false)
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const signOut = useCallback(async () => {
    await logoutRequest()
    setUser(null)
    setLeaguePlayer(null)
  }, [])

  const value = useMemo<SessionValue>(
    () => ({
      user,
      leaguePlayer,
      isAdmin: Boolean(user?.isAdmin),
      loading,
      refresh,
      signOut,
    }),
    [user, leaguePlayer, loading, refresh, signOut]
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const context = useContext(SessionContext)
  if (!context) {
    throw new Error('useSession must be used inside a SessionProvider.')
  }
  return context
}
